import React from "react";
import { motion } from "framer-motion";
import { useReducedMotionSafe } from "@/components/animation/AnimationSettings";
import { PALETTE, riskColor } from "@/components/animation/MotionShared";

/**
 * Animated horizontal risk meter (Low / Medium / High).
 * Premium glassy track with motion fill. Renders a centered label and pill.
 */
export default function AnimatedRiskMeter({
  level = "medium",
  pct,
  label = "RISK",
  testid = "animated-risk-meter",
}) {
  const reduce = useReducedMotionSafe();
  const l = String(level || "medium").toLowerCase();
  const target = typeof pct === "number" ? Math.max(0, Math.min(100, pct))
    : (l === "low" ? 25 : l === "high" ? 80 : 50);
  const color = riskColor(l);

  return (
    <div style={S.shell} data-testid={testid}>
      <header style={S.head}>
        <span style={S.label}>{label}</span>
        <span style={{ ...S.pill, color, borderColor: color, background: `${color}1a` }}>
          {l.toUpperCase()}
        </span>
      </header>
      <div style={S.track} role="progressbar" aria-valuenow={target} aria-valuemin={0} aria-valuemax={100} aria-label={`${label} ${target}%`}>
        <motion.div
          style={{ ...S.fill, background: `linear-gradient(90deg, ${color}, ${PALETTE.gold})` }}
          initial={{ width: "0%" }}
          animate={{ width: `${target}%` }}
          transition={reduce ? { duration: 0 } : { duration: 0.9, ease: "easeOut" }}
        />
        <span style={S.value}>{Math.round(target)}%</span>
      </div>
      <div style={S.ticks} aria-hidden="true">
        <span>Low</span><span>Medium</span><span>High</span>
      </div>
    </div>
  );
}

const S = {
  shell: { display: "flex", flexDirection: "column", gap: 4, width: "100%", minWidth: 0 },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  label: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.18em" },
  pill: { fontFamily: "'Space Mono', monospace", fontSize: 9.5, fontWeight: 800, letterSpacing: "0.10em", padding: "2px 8px", border: "1px solid", borderRadius: 999 },
  track: { position: "relative", width: "100%", height: 14, borderRadius: 999, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.07)", overflow: "hidden" },
  fill: { position: "absolute", inset: "0 auto 0 0", borderRadius: 999, boxShadow: "0 0 14px rgba(197,160,89,0.25)" },
  value: { position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", color: "#0c1a10", fontWeight: 800, fontSize: 10, fontFamily: "'Space Mono', monospace", mixBlendMode: "screen" },
  ticks: { display: "flex", justifyContent: "space-between", color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: "0.10em" },
};
